// Property rows for templated Excel exports — the data excelCellMappings
// (projectProperty sources) resolve from. Separate module so the pure export
// service stays free of the supabase import chain (testable).

import { supabase } from "@/shared/integrations/supabase/client";
import { fetchClientProperties } from "@/features/projects/services/projectsApi";
import type { ExportPropertyInfo } from "./exportSurveyToExcelTemplate";
import { EXPORT_PROPERTY_COLUMNS } from "./exportSurveyToExcelTemplate";

export async function fetchExportProperty(propertyId: string): Promise<ExportPropertyInfo | null> {
  const { data, error } = await supabase
    .from("properties")
    .select(EXPORT_PROPERTY_COLUMNS)
    .eq("id", propertyId)
    .maybeSingle();
  if (error) throw error;
  return data as unknown as ExportPropertyInfo | null;
}

export async function fetchExportProperties(projectId: string): Promise<ExportPropertyInfo[]> {
  return fetchClientProperties<ExportPropertyInfo>(projectId, EXPORT_PROPERTY_COLUMNS);
}

/**
 * The server-export template URL for a property — its own excel_template_url,
 * else its project's — or null. Separate tiny selects (NOT part of
 * EXPORT_PROPERTY_COLUMNS) so an environment without the excel_template_url
 * migrations errors here in isolation and degrades to the local file-pick
 * flow instead of breaking the whole export.
 */
export async function fetchPropertyExportTemplateUrl(
  propertyId: string,
  projectId?: string
): Promise<string | null> {
  const { data, error } = await supabase
    .from("properties")
    .select("excel_template_url")
    .eq("id", propertyId)
    .maybeSingle();
  const own = error
    ? null
    : ((data as { excel_template_url?: string | null } | null)?.excel_template_url ?? null);
  if (own || !projectId) return own;

  const { data: project, error: projErr } = await supabase
    .from("projects")
    .select("excel_template_url")
    .eq("id", projectId)
    .maybeSingle();
  if (projErr) return null;
  return (project as { excel_template_url?: string | null } | null)?.excel_template_url ?? null;
}

/** Storage path → displayable URL (mirrors usePropertySurveyAssets.getPhotoUrl). */
function resolvePhotoUrl(path: string | null): string | null {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  return supabase.storage.from("asset-documents").getPublicUrl(path).data?.publicUrl || null;
}

export interface ExportAssetInfo {
  name: string;
  category: string;
  /** Full-equipment shot. */
  assetPhotoUrl: string | null;
  /** Nameplate close-up. */
  nameplatePhotoUrl: string | null;
  modelNumber: string | null;
  serialNumber: string | null;
  ageYears: number | null;
  manufactureDate: string | null;
  attributes: Record<string, unknown> | null;
}

/** Property assets with photos + nameplate data, for photo slots and the
 * asset-sourced unit answers (units captured as assets, not survey questions). */
export async function fetchExportAssets(propertyId: string): Promise<ExportAssetInfo[]> {
  const { data, error } = await supabase
    .from("property_assets")
    .select(
      "name, asset_categories(name), asset_photo_url, nameplate_photo_url, " +
        "model_number, serial_number, estimated_age_years, manufacture_date_string, category_attributes"
    )
    .eq("property_id", propertyId);
  if (error) throw error;
  return ((data ?? []) as unknown as Array<Record<string, unknown>>).map((row) => ({
    name: String(row.name ?? ""),
    category: String((row.asset_categories as { name?: string } | null)?.name ?? ""),
    assetPhotoUrl: resolvePhotoUrl(row.asset_photo_url as string | null),
    nameplatePhotoUrl: resolvePhotoUrl(row.nameplate_photo_url as string | null),
    modelNumber: (row.model_number as string | null) ?? null,
    serialNumber: (row.serial_number as string | null) ?? null,
    ageYears: row.estimated_age_years != null ? Number(row.estimated_age_years) : null,
    manufactureDate: (row.manufacture_date_string as string | null) ?? null,
    attributes: (row.category_attributes as Record<string, unknown> | null) ?? null,
  }));
}
